import React, {useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {Box, Button, Grid, GridItem, HStack, Text, useDisclosure, useMediaQuery,} from "@chakra-ui/react";
import {use100vh} from "react-div-100vh";
import APIconButton from "../../../components/APIconButton";
import APProgressBar from "../../../components/APProgressBar";
import {FullScreenContext} from "../../../contexts/FullScreenContext";
import {useSettingsContext} from "../../../contexts/SettingsContext";
import {MathProblem, OperatorModel, ValidationStatusModel,} from "../../../types";
import {FormattedMessage, useIntl} from "react-intl";
import {AiOutlineMinus, AiOutlinePlus} from "react-icons/ai";
import APLink from "../../../components/APLink";
import APButton from "../../../components/APButton";
import {RiArrowGoBackLine} from "react-icons/ri";
import DraggableNumberBox from "../../../components/DraggableNumberBox";
import AssistanceModal from "../../../components/AssistanceModal";
import InstructionSidebar from "./InstructionSidebar";
import DroppableNumberBox from "../../../components/DroppableNumberBox";
import {ERASER_LABEL} from "../../../constants";
import ValidationStatus from "./ValidationStatus";
import {convertAnswerString, intlNumber, intlNumberComma} from "../../../utils";
import ValidationStatusIcon from "../../../components/ValidationStatusIcon";
import NavMenu from "../../../components/NavMenu";
import {usePreview} from "react-dnd-preview";
import {FaEraser} from "react-icons/fa";
import {GrPowerReset} from "react-icons/gr";

interface ExerciseScreenProps {
  onValidte: (answerString: string) => void;
  problems: Array<MathProblem>;
  problemIndex: number;
  handleQuit: () => void;
  progress: number;
  currentStep: number;
  correct: number;
  setDisableValidate: Function;
  disableValidate: boolean;
  initialProblemsLength: number;
  onNext: () => void;
  validationStatus: ValidationStatusModel | undefined;
}

export const firstOperandPrefix = "o1_"
export const secondOperandPrefix = "o2_"
export const regroupingOperandPrefix = "regrouping_"
export const answerOperandPrefix = ""

interface ExerciseState {
  first: { input: string, crossed: string, extra: string },
  second: { input: string, crossed: string, extra: string },
  regrouping: { enabled: boolean, input: string, crossed: string, extra: string },
  answer: { input: string, crossed: string, extra: string }
}

function ExerciseScreen(props: ExerciseScreenProps) {
  const {
    onValidte,
    problems,
    problemIndex,
    handleQuit,
    progress,
    currentStep,
    correct,
    initialProblemsLength,
    onNext,
    validationStatus,
  } = props;

  const exercisePlaygroundRef = useRef<HTMLDivElement>(null);
  const [isPlaygroundFocused, setPlaygroundFocused] = useState(false);
  const focusPlayground = () => setPlaygroundFocused(true);
  const blurPlayground = () => setPlaygroundFocused(false);

  const currentProblem = problems[problemIndex];

  const initialPlaygroundActiveCell = {
    value: firstOperandPrefix + (currentProblem.operator === OperatorModel.ADDITION ? "1" : "0"),
    row: firstOperandPrefix,
    cell: currentProblem.operator === OperatorModel.ADDITION ? 1 : 0
  };

  const [playgroundActiveCell, setPlaygroundActiveCell] = useState<{ value: string, row: string, cell: number }>(
    initialPlaygroundActiveCell
  );

  const mainOperationSectionRef = useRef<HTMLDivElement>(null);
  const isTouchDevice = () => "ontouchstart" in document.documentElement;
  const zoomMultiplier = 1.26;

  const maxDecimalLength = useMemo(() => {
    const firstDecimal = currentProblem.operand1.toString().split('.')[1];

    const secondDecimal = currentProblem.operand2.toString().split('.')[1];

    const firstDecimalLength = parseInt(firstDecimal) === 0 ? 0 : firstDecimal?.toString().length;

    const secondDecimalLength = parseInt(secondDecimal) === 0 ? 0 : secondDecimal?.toString().length;

    return Math.max(firstDecimalLength, secondDecimalLength) ?? 1;
  }, [currentProblem]);

  const maxIntLength = useMemo(() => {
    const firstInt = Math.floor(currentProblem.operand1);

    const secondInt = Math.floor(currentProblem.operand2);

    const firstIntLength = firstInt === 0 ? 0 : firstInt.toString().length;

    const secondIntLength = secondInt === 0 ? 0 : secondInt.toString().length;

    return Math.max(firstIntLength, secondIntLength) ?? 1;
  }, [currentProblem]);

  const maxAnswerStringLength = useMemo(() => {
    // +2: 1 is for comma(dot) and another 1 is potential regouping on highest position
    return (maxDecimalLength || 0) + maxIntLength + (
      currentProblem.operator === OperatorModel.ADDITION ?
        currentProblem.operand1.toString().includes('.') ? 2 : 1
        : 1
    ) || 1;
  }, [currentProblem.operator, maxDecimalLength, maxIntLength]);

  const firstOperand = Math.round(currentProblem.operand1 * Math.pow(10, maxDecimalLength) * 100) / 100;
  const secondOperand = Math.round(currentProblem.operand2 * Math.pow(10, maxDecimalLength) * 100) / 100;

  const initialExerciseState = {
    first: {
      input: new Array(maxAnswerStringLength + 1).join(" "),
      crossed: new Array(maxAnswerStringLength + 1).join(" "),
      extra: new Array(maxAnswerStringLength + 1).join(" ")
    },
    second: {
      input: new Array(maxAnswerStringLength + 1).join(" "),
      crossed: new Array(maxAnswerStringLength + 1).join(" "),
      extra: new Array(maxAnswerStringLength + 1).join(" ")
    },
    regrouping: {
      enabled: false,
      input: new Array(maxAnswerStringLength + 1).join(" "),
      crossed: new Array(maxAnswerStringLength + 1).join(" "),
      extra: new Array(maxAnswerStringLength + 1).join(" ")
    },
    answer: {
      input: new Array(maxAnswerStringLength + 1).join(" "),
      crossed: new Array(maxAnswerStringLength + 1).join(" "),
      extra: new Array(maxAnswerStringLength + 1).join(" ")
    }
  };

  const [boxWidth, setBoxWidth] = useState<number>(0);

  const [exerciseState, setExerciseState] = useState<ExerciseState>(initialExerciseState);

  const [exerciseStateHistory, setExerciseStateHistory] = useState<ExerciseState[]>([]);
  const [isRegrouping, setIsRegrouping] = useState(false);

  const digitalBoxSize = useMemo(() => {
    const containerWidth = mainOperationSectionRef.current
      ? mainOperationSectionRef.current.offsetWidth
      : 0;

    const restWidth = containerWidth - 32 - 40 - 40;

    const digitalBoxWidth = Math.floor(restWidth / maxAnswerStringLength);

    return digitalBoxWidth - 8 > 40
      ? "lg"
      : digitalBoxWidth - 8 > 32
      ? "md"
      : "sm";
  }, [mainOperationSectionRef.current, maxAnswerStringLength]);

  const [boxHeight, setBoxHeight] = useState<number>();

  const resetExercise = () => {
    setExerciseState(initialExerciseState);
    setExerciseStateHistory([]);
    setIsRegrouping(false);
  }

  useEffect(() => {
    exercisePlaygroundRef.current?.focus();
  }, [exercisePlaygroundRef.current, currentProblem]);

  const rows = isRegrouping
    ? [regroupingOperandPrefix, firstOperandPrefix, secondOperandPrefix, answerOperandPrefix]
    : [firstOperandPrefix, secondOperandPrefix, answerOperandPrefix];

  const handleKeyPress = (event: KeyboardEvent) => {
    const { key } = event;

    switch (key) {
      case "/": onDragEnd("/", playgroundActiveCell.value, isRegrouping); break;
      case " ": handleActivateRegrouping(); break;
      case "ArrowRight": moveRight(); break;
      case "ArrowLeft": moveLeft(); break;
      case "ArrowDown": moveDown(); break;
      case "ArrowUp": moveUp(); break;
      case "Backspace": onDragEnd(ERASER_LABEL, playgroundActiveCell.value, isRegrouping); break;
      default: handleNumberInput(event); break;
    }
  };

  const moveLeft = () => {
    const isRowStartReached = playgroundActiveCell.cell === 0;
    const { row, cell } = playgroundActiveCell;
    const newCell = isRowStartReached ? cell : cell - 1;
    updatePlaygroundActiveCell(row, newCell);
  };

  const moveRight = (newRowWhenEndReached?: boolean) => {
    const isRowEndReached = playgroundActiveCell.cell + 1 >= maxAnswerStringLength;
    const { row, cell } = playgroundActiveCell;
    const newCell = isRowEndReached ?
      newRowWhenEndReached && playgroundActiveCell.row !== answerOperandPrefix ? 0 : cell : cell + 1;
    const newRow = (isRowEndReached && newRowWhenEndReached) ?
      playgroundActiveCell.row === answerOperandPrefix ?
        playgroundActiveCell.row : rows[rows.findIndex(row => row === playgroundActiveCell.row) + 1]
      : row;
    updatePlaygroundActiveCell(newRow, newCell);
  };

  const moveUp = () => {
    const newRow = playgroundActiveCell.row === rows[0]
      ? playgroundActiveCell.row
      : rows[rows.findIndex(row => row === playgroundActiveCell.row) - 1];
    updatePlaygroundActiveCell(newRow, playgroundActiveCell.cell);
  };

  const moveDown = () => {
    const newRow = playgroundActiveCell.row === answerOperandPrefix
      ? playgroundActiveCell.row
      : rows[rows.findIndex(row => row === playgroundActiveCell.row) + 1];
    updatePlaygroundActiveCell(newRow, playgroundActiveCell.cell);
  };

  const updatePlaygroundActiveCell = (row: string, cell: number) => {
    if (
      currentProblem.operator === OperatorModel.ADDITION &&
      (row === firstOperandPrefix || row === secondOperandPrefix) &&
      cell === 0
    ) cell = cell + 1
    const value = row + cell;
    setPlaygroundActiveCell({ value, row, cell });
  };

  const handleNumberInput = (event: KeyboardEvent) => {
    const { key } = event;
    const regroupingAdditionAction = (
      (key === "+" || key === "1") &&
      currentProblem.operator === OperatorModel.ADDITION &&
      playgroundActiveCell.row === regroupingOperandPrefix
    );
    if (
      currentProblem.operator === OperatorModel.ADDITION &&
      playgroundActiveCell.row === regroupingOperandPrefix &&
      !regroupingAdditionAction
    ) return;

    const validKey =
      !Number.isNaN(parseInt(key)) ||
      key === intlNumberComma(intl.locale) ||
      regroupingAdditionAction;

    if (validKey) onDragEnd(key, playgroundActiveCell.value, isRegrouping);

    if (regroupingAdditionAction) onDragEnd("↑", playgroundActiveCell.value, isRegrouping);
  };

  const onKeyDown = (event: KeyboardEvent) => handleKeyPress(event);

  useEffect(() => {
    if (isPlaygroundFocused) document.addEventListener('keydown', onKeyDown);
    else document.removeEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown, isPlaygroundFocused]);

  useEffect(() => {
    resetExercise();
  }, [problemIndex, maxAnswerStringLength]);

  const correctAnswer =
    currentProblem.operator === OperatorModel.ADDITION
      ? firstOperand + secondOperand
      : firstOperand - secondOperand;

  const correctAnswerString = (
    correctAnswer / Math.pow(10, maxDecimalLength)
  ).toString();

  const intl = useIntl();
  const height = use100vh();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  const { setSettingModalOpen, setProfileOpen, skin, setSettingsTabIndex } = useSettingsContext();
  const {
    isOpen: assistanceModalOpen,
    onClose: onCloseAssistanceModal,
    onOpen: onOpenAssistanceModal,
  } = useDisclosure({ defaultIsOpen: true });

  const handleOpenSettings = useCallback(() => {
    setSettingModalOpen(true);
  }, [setSettingModalOpen]);

  const renderProgress = useCallback(() => {
    return (
      <Box className="progress-section" width={"100%"}>
        <Box display="flex" alignItems={"center"} justifyContent={"center"} columnGap={2}>
          {isLargeScreen && (
            <Box flexGrow={1}>
              <APProgressBar value={progress} />
            </Box>
          )}
          <Text
            fontSize={[isLargeScreen ? "1.1rem" : "0.875rem"]}
            color="#000"
            ml={3}
            fontFamily={"ChromaticaMedium"}
          >
            {
              intl.formatMessage({id: 'progress-bar-label', defaultMessage: "Mot {problem} sur {total}"})
                .replace("{problem}", currentStep?.toString())
                .replace("{total}", initialProblemsLength?.toString())
            }
          </Text>
        </Box>
      </Box>
    );
  }, [progress, initialProblemsLength, correct, currentStep]);

  const renderTopActionButtons = useCallback(() => {
    return (
      <NavMenu
        openProfile={() => setProfileOpen(true)}
        openSettings={() => handleOpenSettings()}
      />
    )
  }, [isLargeScreen, setProfileOpen, handleOpenSettings]);

  const saveStateToHistory = React.useCallback(() => {
    setExerciseStateHistory((exerciseStateHistory) => exerciseStateHistory.concat([exerciseState]));
  }, [exerciseState]);

  const onDragEnd = React.useCallback(
    (label: string, targetBoxName: string, regroupingMode: boolean) => {
      const currentExerciseState = {...exerciseState}
      const updatedValue = (prevValue: string, value: string, prefix?: string) => {
        const boxNumber = prefix ? targetBoxName.replace(prefix, "") : targetBoxName;
        return prevValue.substring(0, Number(boxNumber)) + value + prevValue.substring(Number(boxNumber) + 1);
      }
      const exerciseStateKey =
        targetBoxName.includes(firstOperandPrefix) ? "first" :
        targetBoxName.includes(secondOperandPrefix) ? "second" :
        targetBoxName.includes(regroupingOperandPrefix) ? "regrouping" : "answer"
      const boxNumber = parseInt(targetBoxName.replace(
        targetBoxName.includes(firstOperandPrefix) ? firstOperandPrefix :
        targetBoxName.includes(secondOperandPrefix) ? secondOperandPrefix :
        targetBoxName.includes(regroupingOperandPrefix) ? regroupingOperandPrefix : "", ""
      ))

      const crossing = label === "/"
      const erasing = label === ERASER_LABEL
      const addition = currentProblem.operator === OperatorModel.ADDITION
      const targetingRegrouping = targetBoxName.includes(regroupingOperandPrefix)
      const invalidRegrouping = targetingRegrouping && !regroupingMode
      const boxTaken = exerciseState[exerciseStateKey].input[boxNumber] !== " "
      const crossed = exerciseState[exerciseStateKey].crossed[boxNumber] === "/"
      const extraBoxTaken = exerciseState[exerciseStateKey].extra[boxNumber] !== " "
      const erasingCross = label === ERASER_LABEL && exerciseState[exerciseStateKey].crossed[boxNumber] === "/"
      const nonCrossRegroupingToNormal = regroupingMode && !targetingRegrouping && label !== "/"

      const updatedState = {
        ...exerciseState,
        [exerciseStateKey] : {
          ...exerciseState[exerciseStateKey],
          input: (
            crossing ||
            nonCrossRegroupingToNormal ||
            (targetingRegrouping && boxTaken && !erasing) ||
            (regroupingMode && extraBoxTaken && erasing) ||
            invalidRegrouping ||
            erasingCross
          ) ?
            exerciseState[exerciseStateKey].input :
            updatedValue(
              exerciseState[exerciseStateKey].input,
              label === ERASER_LABEL ? " " : label,
              targetBoxName.includes(firstOperandPrefix) ? firstOperandPrefix :
              targetBoxName.includes(secondOperandPrefix) ? secondOperandPrefix :
              targetBoxName.includes(regroupingOperandPrefix) ? regroupingOperandPrefix : undefined
            ),
          crossed: (
            !(addition && !targetingRegrouping) &&
            !(targetingRegrouping && !regroupingMode) &&
            (crossing || (erasing && crossed))
          ) ?
            updatedValue(
              exerciseState[exerciseStateKey].crossed,
              label === ERASER_LABEL ? " " : "/",
              targetBoxName.includes(firstOperandPrefix) ? firstOperandPrefix :
              targetBoxName.includes(secondOperandPrefix) ? secondOperandPrefix :
              targetBoxName.includes(regroupingOperandPrefix) ? regroupingOperandPrefix : undefined
            ) :
            exerciseState[exerciseStateKey].crossed,
          extra: (
            (regroupingMode && !crossing && !targetingRegrouping) ||
            (targetingRegrouping && boxTaken && regroupingMode && !crossing)
          ) && !(targetingRegrouping && !regroupingMode) && !(erasing && crossed) && !addition ?
            updatedValue(
              exerciseState[exerciseStateKey].extra,
              label === ERASER_LABEL ? " " : label,
              targetBoxName.includes(firstOperandPrefix) ? firstOperandPrefix :
              targetBoxName.includes(secondOperandPrefix) ? secondOperandPrefix :
              targetBoxName.includes(regroupingOperandPrefix) ? regroupingOperandPrefix : undefined
            ) : exerciseState[exerciseStateKey].extra
        }
      }

      if (JSON.stringify(updatedState) !== JSON.stringify(currentExerciseState)) {
        setExerciseState({
          ...updatedState,
          regrouping: {...updatedState.regrouping, enabled: updatedState.regrouping.enabled}
        })
        saveStateToHistory()
        return true;
      }
      return false;
    },
    [saveStateToHistory, exerciseState]
  );

  const validationActive = React.useMemo(() => {
    var commaCount = (exerciseState.answer.input.match(/,/g) || []).length;

    if (commaCount > 1) {
      return false;
    }

    if (!!convertAnswerString(exerciseState.answer.input)) {
      return true;
    }
    return false;
  }, [exerciseState.answer.input]);

  const isBackActive = React.useMemo(() => exerciseStateHistory.length > 0, [exerciseStateHistory]);

  const handleBack = () => {
    setExerciseState(exerciseStateHistory[exerciseStateHistory.length - 1])
    setExerciseStateHistory(exerciseStateHistory.slice(0, exerciseStateHistory.length - 1));
  };

  const handleActivateRegrouping = () => {
    setExerciseState({...exerciseState, regrouping: {
      ...exerciseState.regrouping,
      enabled: !isRegrouping ? true : exerciseState.regrouping.enabled ?
        !(
          !exerciseState.regrouping.extra.replaceAll(" ", "") &&
          !exerciseState.regrouping.crossed.replaceAll(" ", "") &&
          !exerciseState.regrouping.input.replaceAll(" ", "")
        ) : true
    }})
    setIsRegrouping(!isRegrouping);
  };

  const handleClickValidate = React.useCallback(() => {
    setIsRegrouping(false);
    onValidte(exerciseState.answer.input);
    setPlaygroundActiveCell(initialPlaygroundActiveCell);
  }, [exerciseState.answer.input, onValidte]);

  const defaultKeyboard = [
    "0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
    isRegrouping ? "/" : intlNumberComma(intl.locale),
    ERASER_LABEL
  ]

  const DragGhost = () => {
    const preview = usePreview();
    if (!preview.display) return null;
    const item: any = preview.item;
    const label = item.label;
    return (
      <div style={{
        width: isLargeScreen ? "40px" : "48px",
        height: isLargeScreen ? "40px" : "48px",
        border: `1px solid ${isRegrouping ? "#B6DEB0" : "#E7E1D8"}`,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isRegrouping ? "#94DAF3" : "#fff",
        opacity: 1,
        zIndex: 1,
        ...preview.style
      }}>
        {label === ERASER_LABEL ? <FaEraser /> : label === "↑" ? "+1" : label}
      </div>
    )
  }

  return (
    <FullScreenContext.Consumer>
      {({ fullScreen, toggleFullScreen }) => (
        <Box
          className={
            "exercise-screen" +
            (fullScreen || !isLargeScreen ? " full-screen" : "")
          }
          sx={{
            backgroundColor: skin?.data?.color?.iv,
            backgroundImage: skin?.data?.background,
            minHeight: "100vh"
          }}
        >
          {!!validationStatus && (
            <ValidationStatus
              onNext={() => {
                setExerciseState(initialExerciseState)
                onNext()
              }}
              status={validationStatus}
              correctAnswer={correctAnswerString}
            />
          )}
          <Grid
            className={
              isLargeScreen ? "exercise-actions" : "exercise-res-actions"
            }
            templateColumns="repeat(1, 1fr)"
            gap={2}
          >
            <GridItem
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                display: "flex",
                flexWrap: "wrap",
                rowGap: "0.5rem",
              }}
            >
              <Box
                sx={{
                  flex: "1",
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <APIconButton
                  sx={{mr: "auto"}}
                  style={{borderRadius: "12px"}}
                  onClick={handleQuit}
                >
                  <img src={`${process.env.REACT_APP_BASE_URL}/close.png`} alt="close" width={isLargeScreen ? "30px" : "24px"} />
                </APIconButton>
              </Box>
              <Box
                sx={{
                  mr: "auto",
                  mx: 4,
                  flex: "1",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  flexDirection: "column"
                }}
              >
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "22px" : "18px",
                    fontWeight: "bold",
                  }}
                >
                  <FormattedMessage
                    id={currentProblem.operator === OperatorModel.ADDITION ? "addition" : "subtraction"}
                    defaultMessage={currentProblem.operator === OperatorModel.ADDITION ? "Addition" : "Soustraction"}
                  />
                </Box>
              </Box>
              <Box sx={{
                ml: "auto",
                width: ["fit-content", undefined],
                flex: "1",
                display: "flex",
                justifyContent: "center"
              }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    ml: "auto"
                  }}
                >
                  {renderTopActionButtons()}
                </Box>
              </Box>
            </GridItem>
          </Grid>
          <Box
            sx={{
              width: isLargeScreen ? "calc(100% - 32px)" : "calc(100% - 48px)",
              marginTop: isLargeScreen ? "0px" : 0,
              mx: isLargeScreen ? "16px" : "24px",
              position: "relative",
              background: "white",
              borderRadius: isLargeScreen ? "30px" : "12px 12px 0 0",
              zIndex: 1,
              border: isLargeScreen ? "none" : "2px solid #E7E1D8",
              borderBottom: "none"
            }}
          >
            <Box
              sx={{
                fontSize: isLargeScreen ? "22px" : "18px",
                fontWeight: "bold",
                textAlign: "center",
                padding: isLargeScreen ? "20px 30px" : "10px 24px",
                bg: "#fcfcfa",
                borderRadius: "12px 24px",
              }}
            >
              {renderProgress()}
            </Box>
          </Box>
          <Box
            sx={{
              width: isLargeScreen ? "calc(100% - 212px)" : "calc(100% - 48px)",
              marginTop: isLargeScreen ? "20px" : 0,
              marginBottom: isLargeScreen ? "unset" : "44px",
              mx: isLargeScreen ? "106px" : "24px",
              position: "relative",
              borderRadius: "30px",
              display: "flex",
              justifyContent: "center",
              // minHeight: height ? height - (141 + 80 + (isLargeScreen ? 0 : 88)) : "100vh"
              minHeight: height ? height - (isLargeScreen ? 281 : 309) : "100vh"
            }}
          >
            <Grid
              display={isLargeScreen ? "grid" : "block"}
              templateColumns={[
                "repeat(1, 1fr)",
                "repeat(1, 1fr)",
                "repeat(3, 1fr)",
              ]}
              gap={isLargeScreen ? "20px" : 0}
              width="100%"
              sx={{h: "auto"}}
            >
              <GridItem colSpan={[1]}>
                <InstructionSidebar
                  onOpenAssistance={onOpenAssistanceModal}
                  currentProblem={currentProblem}
                  resetExercise={resetExercise}
                />
              </GridItem>
              <GridItem colSpan={[1, 1, 2]} sx={{h: "100%"}} ref={mainOperationSectionRef}>
                <Box
                  sx={{
                    bg: "white",
                    borderRadius: isLargeScreen ? "16px" : "0 0 12px 12px",
                    border: isLargeScreen ? "2px solid #F5F3EF" : "2px solid #E7E1D8",
                    borderTop: "none",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {isLargeScreen && (
                    <Box
                      sx={{
                        padding: "10px 16px",
                        bgColor: "#F5F3EF",
                        borderTopLeftRadius: isLargeScreen ? "16px" : 0,
                        borderTopRightRadius: isLargeScreen ? "16px" : 0,
                        position: "relative",
                        h: "75px",
                        d: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <APIconButton
                        sx={{
                          transform: "scale(-1, 1)",
                          background: "#E7E1D8",
                          boxShadow: "none",
                          w: "55px",
                          h: "55px",
                          position: "absolute",
                          left: "16px"
                        }}
                        onClick={resetExercise}
                      >
                        <GrPowerReset size={isLargeScreen ? 20 : 16}/>
                      </APIconButton>
                      <Text sx={{ fontSize: "20px", color: "#000", fontWeight: 900, textAlign: "center" }}>
                        {
                          `
                            ${intl.formatMessage({id: 'solve', defaultMessage: "Résoudre"})}
                            ${intlNumber(currentProblem.operand1, intl.locale)}
                            ${currentProblem.operator === OperatorModel.ADDITION ? "+" : "-"}
                            ${intlNumber(currentProblem.operand2, intl.locale)}
                          `
                        }
                      </Text>
                    </Box>
                  )}
                  <Box sx={{ padding: isLargeScreen ? "20px" : "16px" }}>
                    <Box sx={{ display: isLargeScreen ? "flex" : "block", columnGap: isLargeScreen ? "20px" : "16px" }}>
                      <Button
                        aria-label="back"
                        isDisabled={!isBackActive}
                        onClick={handleBack}
                        sx={{
                          mb: isLargeScreen ? "20px" : "16px",
                          p: isLargeScreen ? "0 20px" : "0 16px",
                          h: isLargeScreen ? "50px" : "40px"
                        }}
                      >
                        <RiArrowGoBackLine size={isLargeScreen ? 20 : 16} />
                        <Text sx={{ml: isLargeScreen ? 4 : 3, fontSize: isLargeScreen ? "1.26rem" : "1rem"}}>
                          <FormattedMessage id="undo-button" defaultMessage={"Annuler"}/>
                        </Text>
                      </Button>
                      <Box
                        role="button"
                        tabIndex={0}
                        sx={{
                          position: "relative",
                          ml: "auto",
                          w: digitalBoxSize === "sm" ? "100%" : "fit-content",
                          "&:focus": { outline: 0 }
                        }}
                        onFocus={focusPlayground}
                        onBlur={blurPlayground}
                        ref={exercisePlaygroundRef}
                      >
                        <Box sx={{
                          position: "absolute",
                          top: exerciseState.regrouping.enabled ?
                            `${!boxHeight ? "80px" : boxHeight * 2 - 4}px` : `${!boxHeight ? "36px" : boxHeight}px`
                        }}>
                          {currentProblem.operator === OperatorModel.ADDITION ? <AiOutlinePlus /> : <AiOutlineMinus/>}
                        </Box>
                        <Box sx={{ pl: isLargeScreen ? "50px" : "0" }}>
                          <HStack sx={{ justifyContent: "flex-end" }}>
                            {exerciseState.regrouping.enabled && Array.from(
                              Array(maxAnswerStringLength),
                              (_, index) => index
                            ).map((boxIndex: number) => (
                              <DroppableNumberBox
                                name={regroupingOperandPrefix + boxIndex.toString()}
                                label={exerciseState.regrouping.input[boxIndex]}
                                validationStatus={validationStatus}
                                size={digitalBoxSize}
                                width={boxWidth}
                                onBoxHeightChange={(height: number) => setBoxHeight(height)}
                                operator={currentProblem.operator}
                                crossed={exerciseState.regrouping.crossed[boxIndex] === "/"}
                                extraNumber={exerciseState.regrouping.extra[boxIndex]}
                                regroupingMode={isRegrouping}
                                disableMargin={boxIndex === 0}
                                onClick={() => setPlaygroundActiveCell({
                                  value: regroupingOperandPrefix + boxIndex,
                                  row: regroupingOperandPrefix,
                                  cell: boxIndex
                                })}
                                focused={
                                  isPlaygroundFocused &&
                                  playgroundActiveCell.value === regroupingOperandPrefix + boxIndex.toString()
                                }
                              />
                            ))}
                          </HStack>
                        </Box>
                        <Box sx={{ pl: isLargeScreen ? "50px" : "40px" }}>
                          <HStack sx={{ mt: isLargeScreen ? "5px" : "4px", justifyContent: "flex-end" }} ref={ref => {
                            if (ref) {
                              const box = ref.getElementsByTagName('div')[0];
                              setBoxWidth(box.offsetWidth);
                            }
                          }}>
                            {Array.from(
                              Array(maxAnswerStringLength - (
                                currentProblem.operator === OperatorModel.ADDITION ? 1 : 0
                              )),
                              (_, index) => index
                            ).map((boxIndex: number) => {
                              const cellIndex = boxIndex + (currentProblem.operator === OperatorModel.ADDITION ? 1 : 0);
                              return (
                                <DroppableNumberBox
                                  name={firstOperandPrefix + cellIndex.toString()}
                                  label={exerciseState.first.input[cellIndex]}
                                  validationStatus={validationStatus}
                                  size={digitalBoxSize}
                                  onBoxHeightChange={(height: number) => setBoxHeight(height)}
                                  operator={currentProblem.operator}
                                  crossed={exerciseState.first.crossed[cellIndex] === "/"}
                                  extraNumber={exerciseState.first.extra[cellIndex]}
                                  regroupingMode={isRegrouping}
                                  onClick={() => setPlaygroundActiveCell({
                                    value: firstOperandPrefix + cellIndex,
                                    row: firstOperandPrefix,
                                    cell: cellIndex
                                  })}
                                  focused={
                                    isPlaygroundFocused &&
                                    playgroundActiveCell.value === firstOperandPrefix + cellIndex.toString()
                                  }
                                />
                              )
                            })}
                          </HStack>
                          <HStack sx={{ mt: isLargeScreen ? "5px" : "4px", justifyContent: "flex-end" }}>
                            {Array.from(
                              Array(maxAnswerStringLength - (
                                currentProblem.operator === OperatorModel.ADDITION ? 1 : 0
                              )),
                              (_, index) => index
                            ).map((boxIndex: number) => {
                              const cellIndex = boxIndex + (currentProblem.operator === OperatorModel.ADDITION ? 1 : 0);
                              return (
                                <DroppableNumberBox
                                  name={secondOperandPrefix + cellIndex.toString()}
                                  label={exerciseState.second.input[cellIndex]}
                                  validationStatus={validationStatus}
                                  size={digitalBoxSize}
                                  onBoxHeightChange={(height: number) => setBoxHeight(height)}
                                  operator={currentProblem.operator}
                                  crossed={exerciseState.second.crossed[cellIndex] === "/"}
                                  extraNumber={exerciseState.second.extra[cellIndex]}
                                  regroupingMode={isRegrouping}
                                  onClick={() => setPlaygroundActiveCell({
                                    value: secondOperandPrefix + cellIndex,
                                    row: secondOperandPrefix,
                                    cell: cellIndex
                                  })}
                                  focused={
                                    isPlaygroundFocused &&
                                    playgroundActiveCell.value === secondOperandPrefix + cellIndex.toString()
                                  }
                                />
                              )
                            })}
                          </HStack>
                        </Box>

                        <HStack
                          sx={{ mt: isLargeScreen ? "5px" : "4px", borderBottom: "2px solid #000" }}
                        ></HStack>

                        <Box sx={{ pl: isLargeScreen ? "50px" : "0", mt: isLargeScreen ? "5px" : "4px" }}>
                          <HStack sx={{ justifyContent: "flex-end" }}>
                            {Array.from(
                              Array(maxAnswerStringLength),
                              (_, index) => index
                            ).map((boxIndex: number) => (
                              <DroppableNumberBox
                                name={boxIndex.toString()}
                                label={exerciseState.answer.input[boxIndex]}
                                validationStatus={validationStatus}
                                size={digitalBoxSize}
                                width={boxWidth}
                                onBoxHeightChange={(height: number) => setBoxHeight(height)}
                                operator={currentProblem.operator}
                                crossed={exerciseState.answer.crossed[boxIndex] === "/"}
                                regroupingMode={isRegrouping}
                                disableMargin={boxIndex === 0}
                                onClick={() => setPlaygroundActiveCell({
                                  value: boxIndex.toString(),
                                  row: answerOperandPrefix,
                                  cell: boxIndex
                                })}
                                focused={isPlaygroundFocused && playgroundActiveCell.value === boxIndex.toString()}
                              />
                            ))}
                          </HStack>
                          {validationStatus && (
                            <Box
                              display="flex"
                              justifyContent="center"
                              mt={isLargeScreen ? "16px" : "12px"}
                            >
                              <ValidationStatusIcon
                                validationStatus={validationStatus}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      bgColor: "#FCFCFA",
                      px: isLargeScreen ? "16px" : "12px",
                      paddingTop: isLargeScreen ? "16px" : "12px",
                      paddingBottom: isLargeScreen ? "20px" : "16px",
                      justifyContent: "space-between",
                      display: "flex",
                      alignItems: "center",
                      marginTop: "auto",
                      borderRadius: "0 0 12px 12px"
                    }}
                  >
                    <APButton
                      sx={{
                        fontSize: isLargeScreen ? "1.26rem" : "1rem",
                        p: isLargeScreen ? "0 20px" : "0 16px",
                        h: isLargeScreen ? "50px" : "40px"
                      }}
                      onClick={handleActivateRegrouping}
                    >
                      <FormattedMessage
                        id={currentProblem.operator === OperatorModel.ADDITION ? "detention-button" : "loan-button"}
                        defaultMessage={currentProblem.operator === OperatorModel.ADDITION ? "Retenue" : "Emprunt"}
                      />
                    </APButton>
                    <APLink onClick={onOpenAssistanceModal}>
                      <FormattedMessage id="assistance-button" defaultMessage={"Aide"}/>
                    </APLink>
                    <APButton
                      onClick={handleClickValidate}
                      isDisabled={!validationActive}
                      sx={{
                        fontSize: isLargeScreen ? "1.26rem" : "1rem",
                        p: isLargeScreen ? "0 20px" : "0 16px",
                        h: isLargeScreen ? "50px" : "40px"
                      }}
                    >
                      <FormattedMessage id="validate-button" defaultMessage={"Valider"}/>
                    </APButton>
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          </Box>

          <Box
            display="flex"
            justifyContent={isLargeScreen ? "space-between" : "center"}
            height={isLargeScreen ? "80px" : "auto"}
            alignItems={"center"}
            mx="20px"
            py={isLargeScreen ? 0 : '8px'}
          >
            {isLargeScreen && (
              <Button
                sx={{
                  h: "50px",
                  p: "0 20px",
                  fontSize: "1.26rem"
                }}
                onClick={() => {
                  setSettingsTabIndex(1)
                  setProfileOpen(true)
                }}
              >
                <FormattedMessage id="wallpaper" defaultMessage={"Fond d’écran"}/>
              </Button>
            )}
            <HStack
              display={
                isLargeScreen || (isRegrouping && currentProblem.operator === OperatorModel.ADDITION) ? "flex" : "grid"
              }
              gridTemplateColumns="repeat(6, 1fr)"
              flexWrap={"wrap"}
            >
              {(isRegrouping && currentProblem.operator === OperatorModel.ADDITION ?
                ["↑", "/", ERASER_LABEL] : [...defaultKeyboard]
              ).map((label, index) => (
                <DraggableNumberBox onDropEnd={onDragEnd} label={label} regroupingMode={isRegrouping} key={index}/>
              ))}
            </HStack>
          </Box>

          <AssistanceModal
            isOpen={assistanceModalOpen}
            onClose={onCloseAssistanceModal}
            operator={currentProblem.operator}
          />
          {isTouchDevice() ? <DragGhost/> : null}
        </Box>
      )}
    </FullScreenContext.Consumer>
  );
}

export default ExerciseScreen;
